import { Box, IconButton, Typography, useTheme } from "@mui/material";
import {
  CardsIconNav,
  ExchangeIconNav,
  HomeIconNav,
  ProfileIconNav,
  ServicesIconNav,
} from "../../icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigation } from "../../contexts/NavigationContext";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isIOS = Telegram.WebApp.platform === "ios";
  const { isNavigationVisible } = useNavigation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { t } = useTranslation("home");
  // Когда админ выключил фичу `exchange` в /settings, вкладка визуально
  // перестаёт быть «Обменом» — превращается в «Сервисы» (AML, SWIFT,
  // оплата ЖКХ, ин. карты и т.п.). Маршрут `/exchange` при этом
  // сохраняется, чтобы не ломать deep-link'и и историю переходов.
  const exchangeEnabled = isFeatureEnabled("exchange");

  const { isHome, isExchange, isCards, isProfile } = useMemo(
    () => ({
      isHome:
        location.pathname === "/" ||
        location.pathname === "/deposit" ||
        location.pathname === "/withdraw",
      isExchange: location.pathname.startsWith("/exchange"),
      isCards: location.pathname.startsWith("/cards"),
      isProfile: location.pathname === "/profile",
    }),
    [location.pathname]
  );

  const navLinks = useMemo(
    () => [
      {
        label: t("tabs.home"),
        icon: <HomeIconNav selected={isHome} />,
        path: "/",
        isSelected: isHome,
      },
      {
        label: t("tabs.cards"),
        icon: <CardsIconNav selected={isCards} />,
        path: "/cards",
        isSelected: isCards,
      },
      {
        label: exchangeEnabled ? t("tabs.exchange") : t("tabs.services"),
        icon: exchangeEnabled ? (
          <ExchangeIconNav selected={isExchange} />
        ) : (
          <ServicesIconNav selected={isExchange} />
        ),
        path: "/exchange",
        isSelected: isExchange,
      },
      {
        label: t("tabs.profile"),
        icon: <ProfileIconNav selected={isProfile} />,
        path: "/profile",
        isSelected: isProfile,
      },
    ],
    [isHome, isExchange, isCards, isProfile, exchangeEnabled, t]
  );

  const handleClick = (path: string) => {
    navigate(path);
  };

  const bottomPadding = isIOS
    ? "calc(env(safe-area-inset-bottom) + 20px)"
    : "20px";

  const isMaintenancePage = location.pathname === "/maintenance";

  if (isMaintenancePage) {
    return null;
  }

  return (
    <>
      {/* Bottom gradient overlay */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: `calc(${bottomPadding} + 62px + 15px + 60px)`,
          zIndex: 99996,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 100%)"
              : "linear-gradient(0deg, rgba(239, 239, 243, 0.85) 0%, rgba(239, 239, 243, 0.00) 100%)",
          pointerEvents: "none",
          transform: isNavigationVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease-in-out",
        }}
      />
      {/* Blur layer */}
      <Box
        sx={{
          position: "fixed",
          bottom: `calc(${bottomPadding} + 5px)`,
          // Совпадает с горизонтальным padding контента (layout.tsx: 22px),
          // чтобы меню и карточки контента были одной ширины.
          left: 22,
          right: 22,
          zIndex: 99997,
          minHeight: `61px`,
          borderRadius: "60px",
          // В новой чёрной теме bottom-nav лежит на #000 — если оставить
          // старый rgba(20,20,21,0.4) + blur, плашка читается как серая
          // полоса. Берём тон под `paper` (0B0B0D) с чуть большей
          // непрозрачностью, тонкую hairline-обводку и мягкую внешнюю
          // тень — плашка становится «монолитной» с фоном, как карточки
          // на zero-лендинге.
          background:
            theme.palette.mode === "dark"
              ? "rgba(23, 23, 26, 0.85)"
              : "rgba(255, 255, 255, 0.75)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 -6px 24px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.06) inset"
              : "0 5px 30px 0 rgba(0, 0, 0, 0.10), 0 0 0 1px rgba(0, 0, 0, 0.05) inset",
          backdropFilter: "blur(12px)",
          transform: isNavigationVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease-in-out",
        }}
      />
      {/* Content layer */}
      <Box
        component="nav"
        sx={{
          position: "fixed",

          bottom: `calc(${bottomPadding} + 5px)`,

          // Синхронизировано с Blur layer и padding контента (22px).
          left: "22px",
          right: "22px",
          zIndex: 99999,
          height: `61px`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: "2px", sm: "4px" },
          paddingTop: "0px",
          marginTop: "0px",
          background: "transparent",
          transform: isNavigationVisible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease-in-out",
          gap: { xs: "2px", sm: "4px" },
        }}
      >
        {navLinks.map((link) => (
          <IconButton
            key={link.label}
            onClick={() => handleClick(link.path)}
            disableRipple
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.85)"
                  : "rgba(0, 0, 0, 0.55)",
              padding: { xs: "6px 8px", sm: "8px 16px" },
              height: "54px",
              borderRadius: "14px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              WebkitTapHighlightColor: "transparent",
              touchAction: "manipulation",
              userSelect: "none",
              willChange: "transform, opacity",
              transform: "translateZ(0)",
              flex: { xs: "1 1 0", sm: "0 0 auto" },
              minWidth: 0,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: "40px",
                paddingInline: "8px",
                opacity: link.isSelected ? 1 : 0,
                transition:
                  "opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                willChange: "opacity, transform",
              },
              "@media (hover: hover)": {
                "&:hover": {
                  "&::before": {
                    opacity: 1,
                  },
                },
              },
              "@media (hover: none)": {
                "&:active": {
                  transform: "scale(0.95) translateZ(0)",
                },
              },
            }}
          >
            <Box
              className="nav-icon"
              sx={{
                height: "24px",
                width: "24px",
                transition: "transform 0.2s ease-in-out",
                transform: "scale(1) translateZ(0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                willChange: "transform",
                opacity: 1,
                flexShrink: 0,
                "& svg": {
                  maxHeight: "100%",
                  maxWidth: "100%",
                  width: "auto",
                  height: "auto",
                },
              }}
            >
              {link.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                fontSize: "10px",
                lineHeight: { xs: "11px", sm: "12px" },
                color: link.isSelected
                  ? "primary.main"
                  : theme.palette.mode === "dark"
                    ? "rgba(235, 235, 245, 0.6)"
                    : "rgba(0, 0, 0, 0.6)",
                transition: "color 0.2s ease-in-out",
                display: "block",
                opacity: 1,
                pb: 0.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {link.label}
            </Typography>
          </IconButton>
        ))}
      </Box>
    </>
  );
}
